<template>
	<div class="settings-wrapper hidden-group-list-wrapper">
		<BackToSettings />
		<h2 class="title header-30" v-t="'settings_option_hidden_groups'" />
		<div>
			<ul class="hidden-group-list">
				<li v-for="group in groupList" :key="group.id">
					<div :data-value="group.id">
						<div class="hidden-group-content">
							<div class="hidden-group-cat">
								<div v-if="group.category" class="hidden-group-category">
									{{
										group.category.user_category
											? group.category.user_category.alias
											: group.category.title
									}}
								</div>
								<div v-if="group.is_dm" class="hidden-group-dm-title">
									{{ $t('chat_stream_header_title_dm') }}
								</div>
								<div class="hidden-group-title">
									{{ group.alias ? group.alias : group.title }}
								</div>
							</div>
							<div class="button-wrapper">
								<Button
									primary
									@click="unhideGroup(group)"
									:aria-label="$t('chat_drawer_action_unhide')"
									:loading="updatingGroupId === group.id"
								>
									{{ $t('chat_drawer_action_unhide') }}
								</Button>
							</div>
						</div>
					</div>
				</li>
			</ul>
		</div>

		<div v-if="showLoading || noResults" class="hidden-loading">
			<IosSpinner
				v-if="showLoading"
				background="#ffffff"
				spinnerColor="#8a8d93"
				class="connecting-spinner"
			>
				<template #title>{{ $t('common_loader_loading_title') }}</template>
			</IosSpinner>

			<div v-if="noResults && !showLoading" class="no-results">
				{{ $t('settings_hidden_groups_empty_title') }}
			</div>
		</div>
	</div>
</template>

<script>
import store, {
	setMembership,
	getMyMembershipForBubble,
	sortChatsByDisplayName,
} from '@/store'
import { Button } from '@/components/common'
import { get, post } from '@/helpers/api'
import IosSpinner from '@/components/IosSpinner'
import BackToSettings from './BackToSettings'
import { fetchMyMemberships } from '@/api/chat'
import { getErrorText } from '@/helperMethods'

export default {
	components: { IosSpinner, BackToSettings, Button },
	data() {
		return {
			store,
			showLoading: true,
			groupList: [],
			updatingGroupId: null,
		}
	},

	mounted() {
		this.$onEventBus('groupHidden', () => {
			this.getHiddenBubbles()
		})
		this.getHiddenBubbles()
	},

	computed: {
		noResults() {
			return this.groupList.length < 1
		},
	},

	methods: {
		async getHiddenBubbles() {
			try {
				const [hiddenGroups, hiddenDms] = await Promise.all([
					// fetch hidden group memberships
					fetchMyMemberships({
						'filter[chat.is_dm]': 0,
						'filter[hidden]': 1,
						include: 'chat,chat.category.user_category',
					}),
					// fetch hidden dm memberships
					fetchMyMemberships({
						'filter[chat.is_dm]': 1,
						'filter[hidden]': 1,
						include: 'chat,stats,chat.memberships.user,chat.meeting',
					}),
				])

				// Combine and process both groups and DMs
				this.groupList = [...hiddenGroups, ...hiddenDms]
					.map((membership) => ({
						...membership.chat,
						category: membership.chat.category,
					}))
					.sort(sortChatsByDisplayName)

				this.showLoading = false
			} catch (error) {
				console.error(error)
			}
		},
		async unhideGroup(group) {
			this.updatingGroupId = group.id
			try {
				const { data } = await post('/api/v1/membership.update', {
					bubble_id: group.id,
					ishidden: 0,
				})

				// Check if response indicates an error
				if (!data.ok) {
					throw new Error(data.error || 'Failed to unhide group')
				}

				this.groupList = this.groupList.filter((item) => {
					return item.id != group.id
				})

				const membership = getMyMembershipForBubble(group.id)
				membership.ishidden = false
				setMembership(membership)
			} catch (error) {
				console.error(error)
				const membership = getMyMembershipForBubble(group.id)
				membership.ishidden = true
				setMembership(membership)
				this.$alert.error(error)
				// const errorMsg = getErrorText(error.message)
				// this.$alert.error(this.$t(errorMsg))
			}
			this.updatingGroupId = null
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.hidden-group-list-wrapper {
	position: relative;
	padding: 50px 50px 0 50px;
	overflow-y: auto;

	.title {
		color: var(--neutral-800);
		margin: 0 auto;
		text-align: center;
	}
}

.hidden-group-list {
	list-style: none;
	padding: 0;
	margin: 15px auto;
	overflow-y: auto;
	max-width: 800px;
}
.hidden-group-list li {
	padding: 0 16px;
	width: 100%;
	height: 60px;
}

.hidden-group-list li:hover {
	border-radius: 12px;
	background-color: var(--neutral-100);
}

.hidden-group-list li:last-child {
	margin-bottom: 0px;
}

.hidden-group-content {
	height: 60px;
	width: 100%;
	@include flex-spread;
}

.hidden-group-cat {
	display: table-cell;
	vertical-align: middle;
	width: 90%;
}

.hidden-group-title {
	font-family: Avenir-Heavy;
	font-size: 15px;
	margin-right: 50px;
}

.hidden-group-category,
.hidden-group-dm-title {
	font-family: Avenir-Book;
	font-size: 10px;
	text-transform: uppercase;
}

.hidden-loading {
	display: flex;
	justify-content: center;
	align-items: center;
	height: 75vh;
}

.no-results {
	font-size: 14px;
	font-family: Avenir-Medium;
	color: #9498a3;
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.hidden-group-list-wrapper {
		.hidden-group-list li:hover {
			background-color: var(--neutral-0);
		}

		.ios-spinner {
			background: var(--neutral-100) !important;
		}
	}
}
</style>
